import { Box, Button, useTheme } from "@mui/material";
import { FC, useState } from "react";
import { BORDER_RADIUS } from "../constants";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

interface MonthsGridProps {
  selectedYear: number;
  startDate: Date | null;
  endDate: Date | null;
  onMonthSelect: (monthIndex: number) => void;
  earliestSelectableDate?: Date;
  hoverByQuarter?: boolean;
}

export const MonthsGrid: FC<MonthsGridProps> = ({
  selectedYear,
  startDate,
  endDate,
  onMonthSelect,
  earliestSelectableDate,
  hoverByQuarter = false,
}) => {
  const theme = useTheme();
  const [hoveredQuarterStart, setHoveredQuarterStart] = useState<Date | null>(
    null,
  );

  const getQuarterStartDate = (monthIndex: number): Date => {
    const quarterStartMonth = Math.floor(monthIndex / 3) * 3;

    return new Date(Date.UTC(selectedYear, quarterStartMonth, 1));
  };

  const isHoveredQuarter = (monthIndex: number): boolean => {
    if (!hoverByQuarter || !hoveredQuarterStart) return false;

    return (
      getQuarterStartDate(monthIndex).getTime() ===
      hoveredQuarterStart.getTime()
    );
  };

  const isInRange = (monthIndex: number) => {
    if (!startDate) return false;

    const currentMonthDate = new Date(Date.UTC(selectedYear, monthIndex, 1));

    if (!endDate) {
      return (
        currentMonthDate.getTime() ===
        new Date(
          Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), 1),
        ).getTime()
      );
    }

    const startMonthDate = new Date(
      Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), 1),
    );
    const endMonthDate = new Date(
      Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), 1),
    );

    return (
      currentMonthDate >= startMonthDate && currentMonthDate <= endMonthDate
    );
  };

  const getQuarterBorderRadius = (monthIndex: number): number | string => {
    const monthInQuarter = monthIndex % 3;

    if (monthInQuarter === 0) return `${BORDER_RADIUS} 0 0 ${BORDER_RADIUS}`;
    if (monthInQuarter === 2) return `0 ${BORDER_RADIUS} ${BORDER_RADIUS} 0`;

    return 0;
  };

  const getBorderRadius = (
    monthIndex: number,
    isQuarterHovered: boolean,
  ): number | string => {
    if (!isInRange(monthIndex)) {
      return isQuarterHovered
        ? getQuarterBorderRadius(monthIndex)
        : BORDER_RADIUS;
    }

    const currentMonthDate = new Date(Date.UTC(selectedYear, monthIndex, 1));
    const startMonthDate = startDate
      ? new Date(
          Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), 1),
        )
      : null;
    const endMonthDate = endDate
      ? new Date(Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), 1))
      : startMonthDate;

    if (!startMonthDate || !endMonthDate) return 0;

    const isFirst = currentMonthDate.getTime() === startMonthDate.getTime();
    const isLast = currentMonthDate.getTime() === endMonthDate.getTime();

    if (isFirst && isLast) {
      return BORDER_RADIUS;
    }

    if (isFirst) {
      return `${BORDER_RADIUS} 0 0 ${BORDER_RADIUS}`;
    }

    if (isLast) {
      return `0 ${BORDER_RADIUS} ${BORDER_RADIUS} 0`;
    }

    return 0;
  };

  const getMonthBackgroundColor = (
    isMonthInRange: boolean,
    isQuarterHovered: boolean,
  ): string => {
    if (isMonthInRange) {
      return isQuarterHovered
        ? theme.colors.primary[6]
        : theme.colors.primary[5];
    }

    return isQuarterHovered ? theme.colors.grey : "transparent";
  };

  const getMonthHoverBackgroundColor = (
    isQuarterHoverEnabled: boolean,
    monthBackgroundColor: string,
    isMonthInRange: boolean,
  ): string => {
    if (isQuarterHoverEnabled) {
      return monthBackgroundColor;
    }

    return isMonthInRange ? theme.colors.primary[6] : theme.colors.grey;
  };

  const isMonthDisabled = (monthIndex: number): boolean => {
    if (!earliestSelectableDate) return false;

    const lastDayOfMonth = new Date(Date.UTC(selectedYear, monthIndex + 1, 0));

    return lastDayOfMonth < earliestSelectableDate;
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        rowGap: 1,
        p: 2,
      }}
    >
      {MONTHS.map((month, index) => {
        const monthInRange = isInRange(index);
        const quarterHovered = isHoveredQuarter(index);
        const monthBackgroundColor = getMonthBackgroundColor(
          monthInRange,
          quarterHovered,
        );
        const hoveredBackgroundColor = getMonthHoverBackgroundColor(
          hoverByQuarter,
          monthBackgroundColor,
          monthInRange,
        );
        const handleMouseEnter = () => {
          if (hoverByQuarter) {
            setHoveredQuarterStart(getQuarterStartDate(index));
          }
        };

        const handleMouseLeave = () => {
          if (hoverByQuarter) {
            setHoveredQuarterStart(null);
          }
        };

        return (
          <Button
            disabled={isMonthDisabled(index)}
            key={month}
            onClick={() => onMonthSelect(index)}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            sx={{
              backgroundColor: monthBackgroundColor,
              color: monthInRange
                ? theme.colors.defaultBackground
                : theme.colors.text[6],
              "&:hover": {
                backgroundColor: hoveredBackgroundColor,
              },
              width: "100%",
              height: 60,
              borderRadius: getBorderRadius(index, quarterHovered),
              fontSize: 12,
              fontWeight: 500,
              transition: "none",
            }}
          >
            {month}
          </Button>
        );
      })}
    </Box>
  );
};
